"use client"

import { motion } from "framer-motion"
import Image from "next/image"
import Link from "next/link"
import { Mail, MapPin } from "lucide-react"
import { companyInfo, footerLinks, footerServices } from "@/features/home/data"
import { staggerContainer, staggerItem, fadeUp } from "@/lib/motion"
import type { FooterSocialLink } from "@/features/home/types"
import {
  LinkedinIcon, TwitterXIcon, GithubIcon,
  InstagramIcon, YoutubeIcon, DiscordIcon,
} from "@/components/social-icons"

const VP = { once: true, amount: 0.05 }

const iconMap: Record<FooterSocialLink['iconName'], React.ComponentType<{ className?: string }>> = {
  Linkedin: LinkedinIcon,
  Twitter: TwitterXIcon,
  Github: GithubIcon,
  Instagram: InstagramIcon,
  Youtube: YoutubeIcon,
  Discord: DiscordIcon,
}

export function Footer() {
  return (
    <footer className="relative py-12 sm:py-20 border-t border-border/50" role="contentinfo" aria-label="Site alt bilgisi">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-12 sm:mb-16"
        >
          <motion.div variants={staggerItem} className="col-span-2 md:col-span-1 lg:col-span-1">
            <Link href="/" className="flex items-center gap-2 mb-4">
              <div className="relative w-10 h-10 overflow-hidden rounded-lg bg-secondary/10">
                <Image src="/logo.png" alt="FiveLine logo" fill className="object-cover" />
              </div>
              <span className="font-[family-name:var(--font-heading)] text-xl font-bold text-foreground">
                FiveLine
              </span>
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6">
              {companyInfo.description}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {companyInfo.social.map((item) => {
                const Icon = iconMap[item.iconName]
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    aria-label={`FiveLine ${item.label}`}
                    className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                )
              })}
            </div>
          </motion.div>

          <motion.div variants={staggerItem}>
            <h3 className="font-[family-name:var(--font-heading)] text-lg font-semibold text-foreground mb-4">
              Hızlı Bağlantılar
            </h3>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div variants={staggerItem}>
            <h3 className="font-[family-name:var(--font-heading)] text-lg font-semibold text-foreground mb-4">
              Hizmetlerimiz
            </h3>
            <ul className="space-y-3">
              {footerServices.map((service) => (
                <li key={service}>
                  <span className="text-muted-foreground text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div variants={staggerItem}>
            <h3 className="font-[family-name:var(--font-heading)] text-lg font-semibold text-foreground mb-4">
              İletişim
            </h3>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-primary" />
                </div>
                <a href={`mailto:${companyInfo.email}`} className="text-muted-foreground text-sm hover:text-primary transition-colors">
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-primary" />
                </div>
                <span className="text-muted-foreground text-sm">{companyInfo.location}</span>
              </li>
            </ul>
          </motion.div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="pt-8 border-t border-border/50 flex flex-col md:flex-row items-center justify-between gap-4"
        >
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} FiveLine Development. Tüm hakları saklıdır.
          </p>
          <div className="flex items-center gap-6">
            <Link href="/gizlilik-politikasi" className="text-muted-foreground hover:text-primary transition-colors text-sm">
              Gizlilik Politikası
            </Link>
            <Link href="/kullanim-sartlari" className="text-muted-foreground hover:text-primary transition-colors text-sm">
              Kullanım Şartları
            </Link>
          </div>
        </motion.div>

      </div>
    </footer>
  )
}
